import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames/bind';
import { MemoryKeeper } from '../MemoryKeeper';
import { DashboardContactBlock } from '../DashboardContactBlock';
import { useChatView } from '../../shared/use-chat-view';
import { useMemoryKeeperTour } from '../../shared/use-memory-keeper-tour';
import { useRouting } from '../../shared/use-routing';
import { useUser } from '../../shared/use-user';
import { useAuthentication } from '../../shared/use-authentication';
import { saveOnboardingStep } from '../../../lib/use-cases/save-onboarding-step';
import { logError } from '../../../lib/shared/error-logger';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { WELCOME_GUIDE_ONBOARDING_TYPE } from '../../../lib/shared/onboarding-types';
import styles from './DashboardMemoryView.css';

const cx = classnames.bind(styles);

const MEMORY_TAB_TYPES = ['chat', 'memories', 'share'];

const MEMORY_TAB_PAGE_VISITED_EVENTS = {
    chat: EVENT_TYPES.MEMORY_KEEPER_CHAT_PAGE_VISITED,
    memories: EVENT_TYPES.MEMORY_KEEPER_MEMORIES_PAGE_VISITED,
    share: EVENT_TYPES.MEMORY_KEEPER_SHARE_PAGE_VISITED
};

function DashboardMemoryView() {
    const { location, parseLocationSearch } = useRouting();
    const { tab } = parseLocationSearch();
    const [activeTab, setActiveTab] = useState(MEMORY_TAB_TYPES.includes(tab) ? tab : 'chat');
    const [isMemoryKeeperReady, setIsMemoryKeeperReady] = useState(false);
    const { setChatView } = useChatView();
    const { userData } = useUser();
    const { updateUserData } = useAuthentication();
    const isChatTab = activeTab === 'chat';
    const hasSeenTour = Boolean(userData.onboarding?.[WELCOME_GUIDE_ONBOARDING_TYPE]?.isFinished);

    const handleMemoryKeeperReady = useCallback(() => setIsMemoryKeeperReady(true), []);

    const handleTabChange = (nextTab) => {
        setActiveTab(nextTab);
        window.history.replaceState(null, '', `${location.pathname}?tab=${nextTab}`);
    };

    const handleTourFinish = () => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_ONBOARDING_FINISHED);
        saveOnboardingStep({ onboardingType: WELCOME_GUIDE_ONBOARDING_TYPE, isFinished: true })
            .then(updateUserData)
            .catch(logError);
    };

    const handleTourClose = () => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_ONBOARDING_CLOSED);
        saveOnboardingStep({ onboardingType: WELCOME_GUIDE_ONBOARDING_TYPE, isFinished: true })
            .then(updateUserData)
            .catch(logError);
    };

    useMemoryKeeperTour({
        isEnabled: isChatTab && !hasSeenTour && isMemoryKeeperReady,
        onFinish: handleTourFinish,
        onClose: handleTourClose
    });

    useEffect(() => {
        setChatView(activeTab === 'chat');

        return () => setChatView(false);
    }, [activeTab]);

    useEffect(() => {
        logEvent(MEMORY_TAB_PAGE_VISITED_EVENTS[activeTab]);
    }, [activeTab]);

    return (
        <div className={cx('root', { rootChatView: activeTab === 'chat' })}>
            <MemoryKeeper activeTab={activeTab} onTabChange={handleTabChange} onReady={handleMemoryKeeperReady} />
            {!isChatTab && <DashboardContactBlock clickBlock='dashboard_protected' />}
        </div>
    );
}

export {
    DashboardMemoryView
};
